import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
    AppBar,
    Box,
    Button,
    Chip,
    Container,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Toolbar,
    Typography,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import GavelIcon from '@mui/icons-material/Gavel';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupIcon from '@mui/icons-material/Group';
import SecurityIcon from '@mui/icons-material/Security';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from '../../auth/AuthContext';
import logo from '../../assets/images/justitia_2.gif';
import { apiClient } from '../../api/apiClient';

const drawerWidth = 240;

const Layout = ({ children }) => {
    const { token, logout } = useAuth();
    const navigate = useNavigate();
    const isAuthenticated = Boolean(token);
    const [userData, setUserData] = useState(null);
    const location = useLocation();
    const isAdminRoute = location.pathname.startsWith('/admin');
    const [anchorEl, setAnchorEl] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const fetchUserData = async () => {
            if (isAuthenticated && token) {
                try {
                    // Verwende den API-Client ohne expliziten Authorization-Header
                    // Der Interceptor fügt den Token automatisch hinzu
                    const response = await apiClient.get('/auth/me');
                    setUserData(response.data);
                } catch (error) {
                    console.error('Fehler beim Laden der Benutzerdaten:', error);
                }
            } else {
                // Wenn der Benutzer nicht angemeldet ist, setze userData auf null
                setUserData(null);
            }
        };
        fetchUserData();
    }, [isAuthenticated, token]);

    const adminMenuItems = [
        { 
            section: 'Profil',
            items: [
                { text: 'Übersicht', icon: <AccountCircleIcon />, path: '/admin' },
                { text: 'Passwort ändern', icon: <VpnKeyIcon />, path: '/admin/password-change' }
            ]
        },
        {
            section: 'Administration',
            items: [
                { 
                    text: 'Benutzer', 
                    icon: <GroupIcon />, 
                    path: '/admin/users',
                    permission: 'can_view_menu_benutzer'
                },
                { 
                    text: 'Benutzerverwaltung (Landmaster)', 
                    icon: <GroupIcon />, 
                    path: '/admin/landmaster/users',
                    permission: 'is_landmaster'
                },
                { 
                    text: 'Rollen & Rechte', 
                    icon: <SecurityIcon />, 
                    path: '/admin/roles',
                    permission: 'can_view_menu_rights'
                }
            ]
        },
        {
            section: 'Inhalte',
            items: [
                { 
                    text: 'Gerichte', 
                    icon: <GavelIcon />, 
                    path: '/admin/gerichte',
                    permission: 'can_view_menu_gericht'
                },
                { 
                    text: 'Entscheidungen', 
                    icon: <DescriptionIcon />, 
                    path: '/admin/entscheidungen',
                    permission: 'can_view_menu_entscheidung'
                },
                {
                    text: 'Mitteilungen',
                    icon: <NotificationsIcon />,
                    path: '/admin/mitteilungen',
                    permission: 'can_view_menu_mitteilungen'
                },
                {
                    text: 'Berufskunde',
                    icon: <DescriptionIcon />,
                    path: '/admin/berufskunde',
                    permission: 'can_view_menu_berufskunde'
                }
            ]
        }
    ];

    // Prüft, ob der Benutzer die erforderliche Berechtigung hat
    const hasPermission = (permission) => {
        if (!permission) return true;
        return userData?.role?.permissions?.some(p => p.name === permission);
    };

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        handleMenuClose();
        logout();
        setUserData(null);
        navigate('/login');
        setMobileMenuOpen(false);
    };

    const handleMobileMenuOpen = () => {
        setMobileMenuOpen(true);
    };

    const handleMobileMenuClose = () => {
        setMobileMenuOpen(false);
    };

    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location.pathname]);

    const navigationItems = [
        { label: 'Gerichte', path: '/gerichte' },
        { label: 'Entscheidungen', path: '/entscheidungen' },
        { label: 'Mitteilungen', path: '/mitteilungen' },
        { label: 'Berufskunde', path: '/berufskunde' },
    ];

    const authenticatedNavigationItems = isAuthenticated
        ? [...navigationItems, { label: 'Adminbereich', path: '/admin' }]
        : navigationItems;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <AppBar position="static">
                <Toolbar sx={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    position: 'relative'
                }}>
                    {/* Linke Seite: Logo und Titel */}
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        flexShrink: 0
                    }}>
                        <RouterLink to="/">
                            <img 
                                src={logo} 
                                alt="LSG Logo" 
                                style={{ 
                                    height: '50px', 
                                    marginRight: '24px',
                                    display: 'block'
                                }} 
                            />
                        </RouterLink>
                        <Typography
                            variant="h6"
                            component={RouterLink}
                            to="/"
                            sx={{
                                textDecoration: 'none',
                                color: 'inherit',
                                flexShrink: 0
                            }}
                        >
                            Sozialgerichtsbarkeit
                        </Typography>
                    </Box>

                    {/* Mittlere Navigation */}
                    <Box sx={{ 
                        position: { xs: 'static', md: 'absolute' },
                        left: { md: '50%' },
                        transform: { md: 'translateX(-50%)' },
                        display: { xs: 'none', md: 'flex' },
                        gap: 2
                    }}>
                        {authenticatedNavigationItems.map((item) => (
                            <Button
                                key={item.path}
                                color="inherit"
                                component={RouterLink}
                                to={item.path}
                                sx={{
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        transform: 'scale(1.05)',
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                    }
                                }}
                            >
                                {item.label}
                            </Button>
                        ))}
                    </Box>

                    {/* Rechte Seite: Anmeldestatus */}
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        flexShrink: 0
                    }}>
                        <IconButton
                            edge="end"
                            color="inherit"
                            aria-label="Navigation öffnen"
                            onClick={handleMobileMenuOpen}
                            sx={{ display: { xs: 'inline-flex', md: 'none' }, ml: 1 }}
                        >
                            <MenuIcon />
                        </IconButton>
                        {isAuthenticated && userData?.username && (
                            <>
                                <Chip
                                    label={`Angemeldet als: ${userData.username}`}
                                    onClick={handleMenuClick}
                                    sx={{
                                        bgcolor: 'primary.light',
                                        color: 'white',
                                        px: 2,
                                        height: 32,
                                        cursor: 'pointer',
                                        '& .MuiChip-label': {
                                            px: 1,
                                        }
                                    }}
                                />
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={handleMenuClose}
                                >
                                    <MenuItem onClick={handleLogout}>
                                        <ListItemIcon>
                                            <LogoutIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText>Abmelden</ListItemText>
                                    </MenuItem>
                                </Menu>
                            </>
                        )}
                    </Box>
                </Toolbar>
            </AppBar>

            <Drawer
                anchor="right"
                open={mobileMenuOpen}
                onClose={handleMobileMenuClose}
                ModalProps={{ keepMounted: true }}
                sx={{ display: { xs: 'block', md: 'none' } }}
                PaperProps={{ sx: { width: 280 } }}
            >
                <Box
                    role="presentation"
                    sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
                >
                    <Box sx={{ p: 2 }}>
                        <Typography variant="h6">Menü</Typography>
                    </Box>
                    <Divider />
                    <List>
                        {authenticatedNavigationItems.map((item) => (
                            <ListItemButton
                                key={item.path}
                                component={RouterLink}
                                to={item.path}
                                onClick={handleMobileMenuClose}
                            >
                                <ListItemText primary={item.label} />
                            </ListItemButton>
                        ))}
                    </List>
                    {isAuthenticated && userData?.username && (
                        <>
                            <Divider />
                            <List sx={{ mt: 'auto' }}>
                                <ListItem>
                                    <ListItemText
                                        primary="Angemeldet als"
                                        secondary={userData.username}
                                    />
                                </ListItem>
                                <ListItemButton onClick={handleLogout}>
                                    <ListItemIcon>
                                        <LogoutIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Abmelden" />
                                </ListItemButton>
                            </List>
                        </>
                    )}
                </Box>
            </Drawer>

            <Box sx={{ display: 'flex', flex: 1 }}>
                {isAuthenticated && isAdminRoute && (
                    <Drawer
                        variant="permanent"
                        sx={{
                            width: drawerWidth,
                            flexShrink: 0,
                            '& .MuiDrawer-paper': {
                                width: drawerWidth,
                                boxSizing: 'border-box',
                                position: 'relative',
                                height: '100%',
                                bgcolor: 'background.paper',
                                display: 'flex',
                                flexDirection: 'column'
                            },
                        }}
                    >
                        <List sx={{ flex: 1 }}>
                            {adminMenuItems.map((section, index) => (
                                <React.Fragment key={section.section}>
                                    {index > 0 && <Divider />}
                                    <ListItem>
                                        <Typography
                                            variant="overline"
                                            color="textSecondary"
                                            sx={{ fontWeight: 'bold' }}
                                        >
                                            {section.section}
                                        </Typography>
                                    </ListItem>
                                    {section.items.map((item) => (
                                        hasPermission(item.permission) && (
                                            <ListItem
                                                key={item.text}
                                                component={RouterLink}
                                                to={item.path}
                                                button
                                                sx={{
                                                    color: 'inherit',
                                                    textDecoration: 'none',
                                                    '&.active': {
                                                        backgroundColor: 'action.selected',
                                                    },
                                                }}
                                            >
                                                <ListItemIcon>{item.icon}</ListItemIcon>
                                                <ListItemText primary={item.text} />
                                            </ListItem>
                                        )
                                    ))}
                                </React.Fragment>
                            ))}
                        </List>
                        <Divider />
                        <List>
                            <ListItem
                                button
                                onClick={handleLogout}
                                sx={{
                                    color: 'error.main',
                                    '& .MuiListItemIcon-root': {
                                        color: 'error.main'
                                    }
                                }}
                            >
                                <ListItemIcon>
                                    <LogoutIcon />
                                </ListItemIcon>
                                <ListItemText primary="Abmelden" />
                            </ListItem>
                        </List>
                    </Drawer>
                )}

                <Box 
                    component="main" 
                    sx={{ 
                        flexGrow: 1, 
                        p: 0,
                        backgroundColor: (theme) =>
                            theme.palette.mode === 'light'
                                ? theme.palette.grey[100]
                                : theme.palette.grey[900],
                    }}
                >
                    {children}
                </Box>
            </Box>

            <Box
                component="footer"
                sx={{
                    py: 3,
                    px: 2,
                    mt: 'auto',
                    backgroundColor: (theme) =>
                        theme.palette.mode === 'light'
                            ? theme.palette.grey[200]
                            : theme.palette.grey[800],
                }}
            >
                <Container maxWidth="lg">
                    <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: { xs: 1.5, sm: 2 },
                        rowGap: 0.5,
                        mb: 2
                    }}>
                        <Button
                            color="inherit"
                            component={RouterLink}
                            to="/impressum"
                            sx={{ color: 'text.secondary' }}
                        >
                            Impressum
                        </Button>
                        <Button
                            color="inherit"
                            component={RouterLink}
                            to="/impressum"
                            sx={{ color: 'text.secondary' }}
                        >
                            Datenschutz
                        </Button>
                        <Button
                            color="inherit"
                            component={RouterLink}
                            to="/streitwertkatalog"
                            sx={{ color: 'text.secondary' }}
                        >
                            Streitwertkatalog
                        </Button>
                        <Button
                            color="inherit"
                            component={RouterLink}
                            to="/barrierefreiheit"
                            sx={{ color: 'text.secondary' }}
                        >
                            Barrierefreiheit
                        </Button>
                        <Button
                            color="inherit"
                            component={RouterLink}
                            to="/kontakt"
                            sx={{ color: 'text.secondary' }}
                        >
                            Kontakt
                        </Button>
                        <Button
                            color="inherit"
                            component={RouterLink}
                            to={isAuthenticated ? "/admin" : "/login"}
                            sx={{ color: 'text.secondary' }}
                        >
                            {isAuthenticated ? "Admin" : "Login"}
                        </Button>
                    </Box>
                    <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        align="center"
                        sx={{ px: { xs: 1, sm: 0 }, wordBreak: 'break-word' }}
                    >
                        © {new Date().getFullYear()} Sozialgerichtsbarkeit
                    </Typography>
                </Container>
            </Box>
        </Box>
    );
};

export default Layout; 